import { env } from 'src/types/common';
import { ErrorHandler } from './errorHandler';

const apiBaseUrl =
  typeof env.VITE_API_URL === 'string'
    ? env.VITE_API_URL
    : 'https://demo-backend.fynex.ai';
const wsUrl =
  typeof env.VITE_WS_URL === 'string'
    ? env.VITE_WS_URL
    : `${apiBaseUrl.replace(/^http/, 'ws')}/ws`;

export { apiBaseUrl, wsUrl };

export interface WebSocketMessage {
  type: string;
  content?: unknown;
  error?: string;
  [key: string]: unknown;
}

export interface WebSocketConfig {
  onOpen?: () => void;
  onMessage?: (message: WebSocketMessage) => void;
  onError?: (error: Event) => void;
  onClose?: () => void;
  maxReconnectAttempts?: number;
  reconnectDelay?: number;
}

export class WebSocketManager {
  private ws: WebSocket | null = null;
  private config: WebSocketConfig;
  private reconnectAttempts = 0;
  private maxReconnectAttempts: number;
  private reconnectDelay: number;
  private messageQueue: WebSocketMessage[] = [];

  constructor(config: WebSocketConfig = {}) {
    this.config = config;
    this.maxReconnectAttempts = config.maxReconnectAttempts || 5;
    this.reconnectDelay = config.reconnectDelay || 1000;
  }

  get readyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  get socket(): WebSocket | null {
    return this.ws;
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }

      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          this.reconnectAttempts = 0;
          this.config.onOpen?.();

          // Send any queued messages
          while (this.messageQueue.length > 0) {
            const message = this.messageQueue.shift();
            this.send(message);
          }

          resolve();
        };

        this.ws.onmessage = event => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            this.config.onMessage?.(message);
          } catch (error) {
            ErrorHandler.handleError(error, {
              showNotification: false,
              logToConsole: true,
            });
          }
        };

        this.ws.onerror = error => {
          ErrorHandler.handleError(error, {
            showNotification: false,
            logToConsole: true,
          });
          this.config.onError?.(error);
          reject(error);
        };

        this.ws.onclose = () => {
          this.config.onClose?.();

          // Auto-reconnect if configured
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts += 1;
            const delay = Math.min(
              this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
              30000
            );
            setTimeout(() => this.connect(), delay);
          }
        };
      } catch (error) {
        ErrorHandler.handleError(error, {
          showNotification: false,
          logToConsole: true,
        });
        reject(error);
      }
    });
  }

  send(data: WebSocketMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // Queue the message for later sending
      this.messageQueue.push(data);
      return false;
    }

    try {
      this.ws.send(typeof data === 'string' ? data : JSON.stringify(data));
      return true;
    } catch (error) {
      ErrorHandler.handleError(error, {
        showNotification: false,
        logToConsole: true,
      });
      return false;
    }
  }

  close(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.messageQueue = [];
  }
}
